/* eslint-disable no-underscore-dangle */
import React from 'react';
import {
  Card, Form, Table, Button, Badge, Spinner,
} from 'react-bootstrap';
import { formatBookingDate } from '../../../Utils/timezone';

const SYNC_FIELDS = [
  // Contact & Status
  { key: 'full_name', label: 'Passenger Name', group: 'Contact & Status' },
  { key: 'email_address', label: 'Email', group: 'Contact & Status' },
  { key: 'phone_number', label: 'Phone', group: 'Contact & Status' },
  { key: 'status', label: 'Status', group: 'Contact & Status' },
  // Reference & Comments
  {
    key: 'partner_booking_reference',
    label: 'Partner Booking Reference',
    group: 'Reference & Comments',
  },
  { key: 'comments', label: 'Comments', group: 'Reference & Comments' },
  { key: 'admin_comments', label: 'Admin Comments', group: 'Reference & Comments' },
  // Locations
  {
    key: 'destination_from',
    label: 'Destination From',
    group: 'Locations',
    isObject: true,
  },
  {
    key: 'destination_to',
    label: 'Destination To',
    group: 'Locations',
    isObject: true,
  },
  // Programme
  {
    key: 'programme',
    label: 'Programme/Itinerary',
    group: 'Programme',
    isObject: true,
  },
  // Passengers
  {
    key: 'passengers',
    label: 'Passengers (All)',
    group: 'Passengers',
    isObject: true,
  },
  // Billing
  {
    key: 'billing',
    label: 'Billing Details',
    group: 'Billing',
    isObject: true,
  },
];

function LinkedBookingsSyncStep({
  order,
  linkedBookings,
  syncOptions,
  onSyncOptionsChange,
  onBack,
  onContinue,
  loading = false,
}) {
  const handleToggle = (field) => {
    onSyncOptionsChange({
      ...syncOptions,
      [field]: !syncOptions[field],
    });
  };

  const handleSelectAll = () => {
    const allSelected = SYNC_FIELDS.every((f) => syncOptions[f.key]);
    const newOptions = {};
    SYNC_FIELDS.forEach((f) => {
      newOptions[f.key] = !allSelected;
    });
    onSyncOptionsChange(newOptions);
  };

  // Format display value for a field
  const formatFieldValue = (value, field) => {
    if (value === null || value === undefined) return '-';

    if (field.isObject) {
      if (field.key === 'destination_from' || field.key === 'destination_to') {
        return value.address || '-';
      }
      if (field.key === 'passengers') {
        const parts = [];
        if (value.adults) parts.push(`${value.adults} adults`);
        if (value.children) parts.push(`${value.children} children`);
        if (value.infant_carrier) parts.push(`${value.infant_carrier} infant`);
        if (value.booster_seat) parts.push(`${value.booster_seat} booster`);
        if (value.convertible_seat) parts.push(`${value.convertible_seat} convertible`);
        return parts.length > 0 ? parts.join(', ') : '-';
      }
      if (field.key === 'programme') {
        if (!value.enabled) return 'Not enabled';
        const stopCount = value.stops?.length || 0;
        return `${value.total_hours || 0}h, ${stopCount} stops`;
      }
      if (field.key === 'billing') {
        return value.company_name || value.name || '-';
      }
      return `${JSON.stringify(value).substring(0, 50)}...`;
    }

    return value || '-';
  };

  // Determine which fields will be updated for each linked booking
  const getUpdatesForBooking = (linkedBooking) => {
    const updates = [];
    SYNC_FIELDS.forEach((field) => {
      if (syncOptions[field.key]) {
        if (field.isObject) {
          // For objects, compare JSON strings
          if (JSON.stringify(order[field.key]) !== JSON.stringify(linkedBooking[field.key])) {
            updates.push(field.label);
          }
        } else if (order[field.key] !== linkedBooking[field.key]) {
          updates.push(field.label);
        }
      }
    });
    return updates;
  };

  // Group fields for display
  const groupedFields = SYNC_FIELDS.reduce((acc, field) => {
    const group = field.group || 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(field);
    return acc;
  }, {});

  const allSelected = SYNC_FIELDS.every((f) => syncOptions[f.key]);
  const anySelected = SYNC_FIELDS.some((f) => syncOptions[f.key]);

  return (
    <div className="linked-bookings-sync-step">
      <Card className="mb-4">
        <Card.Header className="bg-warning bg-opacity-10">
          <div className="d-flex align-items-center">
            <i className="fa fa-link me-2 text-warning" />
            <strong>Sync to Linked Bookings</strong>
            <Badge bg="secondary" className="ms-2">
              {linkedBookings.length}
              {' '}
              linked
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-3">
            This booking has
            {' '}
            {linkedBookings.length}
            {' '}
            linked booking(s).
            Select which fields to sync to all linked bookings.
          </p>

          {/* Current Booking Values with Sync Checkboxes - Grouped */}
          <Card className="mb-4">
            <Card.Header className="py-2 bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <strong>Current Booking Values</strong>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-decoration-none"
                  onClick={handleSelectAll}
                >
                  {allSelected ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="py-2">
              {Object.entries(groupedFields).map(([groupName, fields]) => (
                <div key={groupName} className="mb-3">
                  <h6 className="text-muted mb-2 border-bottom pb-1">
                    <small>{groupName}</small>
                  </h6>
                  {fields.map((field) => (
                    <div
                      key={field.key}
                      className="d-flex justify-content-between align-items-center py-2 border-bottom"
                    >
                      <div className="text-truncate me-3" style={{ maxWidth: '70%' }}>
                        <span className="text-muted me-2">
                          {field.label}
                          :
                        </span>
                        <strong>{formatFieldValue(order[field.key], field)}</strong>
                      </div>
                      <Form.Check
                        type="switch"
                        id={`sync-${field.key}`}
                        label="Sync"
                        checked={syncOptions[field.key]}
                        onChange={() => handleToggle(field.key)}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </Card.Body>
          </Card>

          {/* Linked Bookings Preview Table */}
          <h6 className="mb-2">Linked Bookings Preview</h6>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
              <span className="ms-2 text-muted">Loading linked bookings...</span>
            </div>
          ) : (
            <Table striped bordered hover size="sm" responsive>
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Date</th>
                  <th>Current Name</th>
                  <th>Current Email</th>
                  <th>Current Status</th>
                  <th>Will Update</th>
                </tr>
              </thead>
              <tbody>
                {linkedBookings.map((booking) => {
                  const updates = getUpdatesForBooking(booking);
                  return (
                    <tr key={booking._id}>
                      <td>
                        <strong>{booking.booking_reference}</strong>
                      </td>
                      <td>{formatBookingDate(booking)}</td>
                      <td>{booking.full_name || '-'}</td>
                      <td>
                        <small>{booking.email_address || '-'}</small>
                      </td>
                      <td>
                        <Badge
                          bg={booking.status === 'Confirmed' ? 'success' : 'secondary'}
                        >
                          {booking.status}
                        </Badge>
                      </td>
                      <td>
                        {updates.length > 0 ? (
                          <span className="text-success">
                            <i className="fa fa-check me-1" />
                            {updates.join(', ')}
                          </span>
                        ) : (
                          <span className="text-muted">
                            <i className="fa fa-minus me-1" />
                            No changes
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          {anySelected && (
            <div className="alert alert-info mt-3 mb-0">
              <i className="fa fa-info-circle me-2" />
              Selected fields will be synced to all
              {' '}
              {linkedBookings.length}
              {' '}
              linked booking(s) when you save.
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Navigation Buttons */}
      <div className="sticky-modal-footer justify-content-between">
        <Button variant="outline-secondary" onClick={onBack}>
          <i className="fa fa-arrow-left me-2" />
          Back to Form
        </Button>
        <Button variant="primary" onClick={onContinue}>
          Continue to Preview
          <i className="fa fa-arrow-right ms-2" />
        </Button>
      </div>
    </div>
  );
}

export default LinkedBookingsSyncStep;
